import { useCallback, useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Row, Col, Button, Spin, Modal, message, Empty } from "antd";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import { LineChartOutlined } from "@ant-design/icons";
import { consultationService } from "../../../services/medicalConsultation";
import LowWeight from "../../../images/LowWeight.png";
import NormalWeight from "../../../images/NormalWeight.png";
import OverWeight from "../../../images/OverWeight.png";
import Obesity from "../../../images/Obesity.png";
import moment from "moment";
import { handlers } from "../../../helpers/helpers";
import * as _ from "lodash";
import { downloadReport } from "./../../../services/general";
import { useTranslation } from "react-i18next";

const ClinicalRecordModal = ({
  id,
  isModalVisible,
  setIsModalVisible,
  patientFullName,
  countryCode,
  countrySettings,
}) => {
  const [loading, setLoading] = useState({
    services: false,
    report: false,
    measures: false,
  });
  const [performedServices, setPerformedServices] = useState();
  const [weights, setWeights] = useState([]);
  const [glycemias, setGlycemias] = useState([]);
  const [pams, setPams] = useState([]);
  const [imcs, setImcs] = useState([]);
  const [heights, setHeights] = useState([]);
  const { t } = useTranslation();

  const location = useLocation();

  const fetchPerformedServices = useCallback(
    async (params) => {
      setLoading((loading) => {
        return { ...loading, services: true };
      });
      let response = await consultationService.getServicesPerformed(params);

      if (response.length > 0) {
        response = _.orderBy(
          response,
          [
            (date) => {
              return moment(date.done_at).format("DD-MM-YYYY");
            },
          ],
          ["asc"]
        );
      }

      setPerformedServices(response);
      setLoading((loading) => {
        return { ...loading, services: false };
      });
    },
    [id]
  );

  const fetchLastMeasurements = useCallback(
    async (params) => {
      setLoading((loading) => {
        return { ...loading, measures: true };
      });
      let response = await consultationService.getLastMeasurements(params);

      if (response.length > 0) {
        response = _.groupBy(response, (item) => item.measurement);

        for (const key in response) {
          if (key === "Glicemia capilar") {
            let glycemia = response["Glicemia capilar"];
            glycemia = _.orderBy(
              glycemia,
              [
                (item) => {
                  return moment(item.date);
                },
              ],
              ["asc"]
            );

            glycemia.map((item) => {
              return (
                (item["value"] = parseFloat(item["value"])),
                (item["date"] = moment(item["date"]).format("DD-MM-YYYY"))
              );
            });

            glycemia.length > 4
              ? setGlycemias(glycemia.slice(-3))
              : setGlycemias(glycemia);
          }

          if (key === "Peso") {
            let weight = response["Peso"];
            weight = _.orderBy(
              weight,
              [
                (item) => {
                  return moment(item.date);
                },
              ],
              ["asc"]
            );

            weight.map((item) => {
              return (
                (item["value"] = parseFloat(item["value"])),
                (item["date"] = moment(item["date"]).format("DD-MM-YYYY"))
              );
            });

            weight.length > 4
              ? setWeights(weight.slice(-3))
              : setWeights(weight);
          }

          if (key === "PAM") {
            let pam = response["PAM"];
            pam = _.orderBy(
              pam,
              [
                (item) => {
                  return moment(item.date);
                },
              ],
              ["asc"]
            );

            pam.map((item) => {
              return (
                (item["value"] = parseFloat(item["value"])),
                (item["date"] = moment(item["date"]).format("DD-MM-YYYY"))
              );
            });

            pam.length > 4 ? setPams(pam.slice(-3)) : setPams(pam);
          }

          if (key === "IMC") {
            let imc = response["IMC"];
            imc = _.orderBy(
              imc,
              [
                (item) => {
                  return moment(item.date);
                },
              ],
              ["asc"]
            );

            imc.map((item) => {
              return (
                (item["value"] = parseFloat(item["value"])),
                (item["date"] = moment(item["date"]).format("DD-MM-YYYY"))
              );
            });

            imc.length > 4 ? setImcs(imc.slice(-3)) : setImcs(imc);
          }

          if (key === "Talla") {
            let height = response["Talla"];
            height = _.orderBy(
              height,
              [
                (item) => {
                  return moment(item.date);
                },
              ],
              ["asc"]
            );

            height.map((item) => {
              return (
                (item["value"] = parseFloat(item["value"])),
                (item["date"] = moment(item["date"]).format("DD-MM-YYYY"))
              );
            });

            setHeights(height);
          }
        }
      }

      setLoading((loading) => {
        return { ...loading, measures: false };
      });
    },
    [id]
  );

  useEffect(() => {
    fetchPerformedServices({ admissionPatientId: id });
    fetchLastMeasurements({ admissionPatientId: id });
  }, [fetchPerformedServices]);

  const closeModal = () => {
    setIsModalVisible((isModalVisible) => {
      return { ...isModalVisible, record: false };
    });
  };

  const formatDate = (date) => {
    return moment(date).format("DD-MM-YYYY");
  };

  const doDownloadReport = async (id, type, occupationalType) => {
    try {
      await handlers.downloadSignedReport({
        id,
        type,
        fileName: 'informe.pdf',
        action: 'open',
      })
    } catch (error) {
      console.error(error);
      message.error({
        content: "Error al descargar informe",
        style: {
          marginTop: '40vh',
        },
      });
    }
  };

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div
          className="p-5"
          style={{ backgroundColor: "rgba(186, 186, 186, 0.15)" }}
        >
          <p className="mb-0" style={{ fontFamily: 10 }}>
            {label}
          </p>
          <p className="mb-0" style={{ fontFamily: 10 }}>
            {payload[0]["payload"]["measurement"] === "Peso" &&
              `${payload[0]["payload"]["value"]} Kg`}
            {payload[0]["payload"]["measurement"] === "Glicemia capilar" &&
              `${payload[0]["payload"]["value"]} mg/dL`}
            {payload[0]["payload"]["measurement"] === "PAM" &&
              `${payload[0]["payload"]["value"]} mmHg`}
            {payload[0]["payload"]["measurement"] === "IMC" &&
              `${payload[0]["payload"]["value"]} Kg/m²`}
          </p>
        </div>
      );
    }

    return null;
  };

  const measurementChart = (dataSource, type) => {
    return (
      <Row className="item-wrapper p-10">
        <Col lg={23} xs={24}>
          {type === "weight" && (
            <p className="ml-5">
              {t('medicalConsultations.clinicalRecordModal.weightTitle')} <strong>{dataSource.slice(-1)[0]["value"]} Kg</strong>
            </p>
          )}
          {type === "glycemia" && (
            <p className="ml-5">
              {" "}
              {t('medicalConsultations.clinicalRecordModal.glycemiaTitle')} <strong>{dataSource.slice(-1)[0]["value"]} mg/dL</strong>
            </p>
          )}
          {type === "pams" && (
            <p className="ml-5">
              {t('medicalConsultations.clinicalRecordModal.pamTitle')} <strong>{dataSource.slice(-1)[0]["value"]} mmHg</strong>
            </p>
          )}
          {type === "imcs" && (
            <p className="ml-5">
              {t('medicalConsultations.clinicalRecordModal.imcTitle')} <strong>{dataSource.slice(-1)[0]["value"]} Kg/m²</strong>
            </p>
          )}
        </Col>
        <Col lg={24} xs={24}>
          <ResponsiveContainer width="95%" height={200}>
            <LineChart
              width={450}
              height={300}
              data={dataSource}
              margin={{
                top: 5,
                right: 10,
                left: -20,
                bottom: 5,
              }}
            >
              <CartesianGrid strokeDasharray="0" />
              <XAxis dataKey="date" />
              <YAxis />
              <Tooltip content={CustomTooltip} />
              <Line
                strokeWidth={2}
                type="monotone"
                dataKey="value"
                stroke="#223A70"
                dot={{ stroke: "#223A70", strokeWidth: 5, r: 2 }}
              />
            </LineChart>
          </ResponsiveContainer>
        </Col>
      </Row>
    );
  };

  const imcType = (value) => {
    const lastImc = value.slice(-1)[0]["value"];

    return (
      <Row justify="center" align="middle">
        <Col
          className="image-wrapper"
          lg={24}
          xs={24}
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          {lastImc <= 18.5 && <img src={LowWeight} height="400" alt="Peso bajo" />}
          {lastImc > 18.5 && lastImc <= 24.9 && (
            <img src={NormalWeight} gutter={15} height="400" alt="Peso normal" />
          )}
          {lastImc >= 25.0 && lastImc <= 29.9 && (
            <img src={OverWeight} height="400" alt="Peso elevado" />
          )}
          {lastImc >= 30.0 && <img src={Obesity} height="400" alt="Obesidad" />}
        </Col>
        <Col lg={24} xs={24}>
          <p className="text-center mt-15 mb-0">
            Peso:
            {lastImc <= 18.5 && " Delgadez severa"}
            {lastImc > 18.5 && lastImc <= 24.9 && " Normal"}
            {lastImc >= 25.0 && lastImc <= 29.9 && " Sobrepeso"}
            {lastImc >= 30.0 && " Obesidad"}
          </p>
        </Col>
      </Row>
    );
  };

  const customTableHeader = () => {
    return (
      <Row className="table-header">
        <Col
          lg={3}
          className="align-item-center-start p-10"
        >
          <h3>Fecha</h3>
        </Col>
        <Col
          lg={11}
          className="p-10"
        >
          <h3 className="text-center">Batería o examen</h3>
        </Col>
        <Col
          lg={4}
          className="center-item p-10"
        >
          <h3 className="text-center">Resultado</h3>
        </Col>
        <Col lg={3} />
        <Col lg={3} />
      </Row>
    );
  };

  const customTableBody = (data, index) => {
    return (
      <Row className="table-body mb-15">
        <Col
          lg={3}
          xs={24}
          className="align-item-center-start p-10"
        >
          <h3 className="hide-desktop text-center">
            Fecha
          </h3>
          <p className="mb-0">{formatDate(data.done_at)}</p>
        </Col>
        <Col
          lg={11}
          xs={24}
          className="center-item p-10"
        >
          <h3 className="hide-desktop text-center">Batería o examen</h3>
          <p className="mb-0">{data.service}</p>
        </Col>
        <Col
          lg={4}
          xs={24}
          className="center-item p-10"
        >
          <h3 className="hide-desktop text-center">Resultado</h3>
          <p
            className={`mb-0 text-center ${data.result === "approved" ? "approved" : "reprobate"
              }`}
          >
            {data.conclusion_text || (data.result === "approved" ? "Aprobado" : "Reprobado")}
          </p>
        </Col>
        <Col
          lg={3}
          xs={24}
          className="center-item p-10"
        >
          <Button
            loading={loading.report}
            onClick={() =>
              doDownloadReport(
                data.id,
                data.signed_at ? "signed" : "generated"
              )
            }
            className="cmt-button secondary outline"
            type="primary"
            size="small"
          >
            Informe
          </Button>
        </Col>
        <Col
          lg={3}
          xs={24}
          className="center-item p-10"
        >
          <Link
            to={{
              pathname: `/consulta-medica/${data.id}`,
              ...(countrySettings.clinicalRecord.disabledOldMedicalConsultation[countryCode]
                && { search: '?mode=disabled' }),
              state: { from: location.pathname, search: location.search },
            }}
            style={{ width: '100%' }}
          >
            <Button
              onClick={closeModal}
              className="cmt-button secondary"
              type="primary"
              size="small"
            >
              Ver detalle
            </Button>
          </Link>
        </Col>
      </Row>
    );
  };


  return (
    <Modal
      open={isModalVisible.record}
      onCancel={closeModal}
      centered
      closable
      footer={false}
      width={"90vw"}
      style={{ maxHeight: "90vh" }}
      className="clinical-record-modal"
    >
      <Row
        justify="center"
        className="pt-15 pb-15 pl-lg-30 pr-lg-30"
        gutter={[0, 25]}
      >
        <Col lg={24} xs={24}>
          <Row justify="space-between" align="middle">
            <Col>
              <h2 className="mb-0">{t('medicalConsultations.clinicalRecordModal.recordTitle')}</h2>
            </Col>
            <Col>
              <p className="mb-0">
                {t('medicalConsultations.clinicalRecordModal.patientTitle')}:{" "}
                <strong className="ml-5">
                  {patientFullName && patientFullName}
                </strong>
              </p>
            </Col>
          </Row>
        </Col>
        <Col lg={24} xs={24}>
          <Row>
            <Col lg={24} xs={24}>
              <h3 className="mb-5">{t('medicalConsultations.clinicalRecordModal.performedServicesTitle')}</h3>
            </Col>
            <Col lg={24} xs={24}>
              {
                loading.services ?
                  <Row justify="center">
                    <Spin className="custom-spin mt-20" />
                  </Row>
                  :
                  performedServices && performedServices.length > 0 ? (
                    <>
                      {customTableHeader()}
                      {
                        performedServices.map((data, index) => {
                          return (
                            <div key={index} >
                              {customTableBody(data, index)}
                            </div>
                          )
                        })
                      }
                    </>
                  ) : (
                    <Row justify="center">
                      <Empty
                        description={
                          <span style={{ fontSize: 12 }}>
                            {t('medicalConsultations.clinicalRecordModal.servicesDisclaimer')}
                          </span>
                        }
                        image={Empty.PRESENTED_IMAGE_SIMPLE}
                      />
                    </Row>
                  )}
            </Col>
          </Row>
        </Col>

        <Col lg={24} xs={24}>
          <Row>
            <Col lg={24} xs={24}>
              <h3 className="mb-20">{t('medicalConsultations.clinicalRecordModal.lastMeasurementsTitle')}</h3>
            </Col>
            <Col lg={24} xs={24}>
              {loading.measures ? (
                <Row justify="center">
                  <Spin className="custom-spinner mt-20" />
                </Row>
              ) : (
                <Row
                  className="measurements-wrapper"
                  justify="space-between"
                  gutter={[20, 20]}
                >
                  <Col lg={15} xs={24}>
                    <Row className="mb-15" gutter={[15, 15]}>
                      <Col lg={12} xs={24}>
                        {weights && weights.length > 0 ? (
                          <Row>
                            <Col lg={24} xs={24}>
                              {measurementChart(weights, "weight")}
                            </Col>
                          </Row>
                        ) : (
                          <Row
                            justify="center"
                            align="middle"
                            style={{ width: "100%", height: "100%" }}
                          >
                            <Col lg={24} xs={24}>
                              <Empty
                                description={
                                  <span style={{ fontSize: 14 }}>
                                    {t('medicalConsultations.clinicalRecordModal.weightDisclaimer')}
                                  </span>
                                }
                                image={
                                  <LineChartOutlined style={{ fontSize: 50 }} />
                                }
                              />
                            </Col>
                          </Row>
                        )}
                      </Col>
                      <Col lg={12} xs={24}>
                        {glycemias && glycemias.length > 0 ? (
                          <Row>
                            <Col lg={24} xs={24}>
                              {measurementChart(glycemias, "glycemia")}
                            </Col>
                          </Row>
                        ) : (
                          <Row
                            justify="center"
                            align="middle"
                            style={{ width: "100%", height: "100%" }}
                          >
                            <Col lg={24} xs={24}>
                              <Empty
                                description={
                                  <span style={{ fontSize: 14 }}>
                                    {t('medicalConsultations.clinicalRecordModal.glycemiaDisclaimer')}
                                  </span>
                                }
                                image={
                                  <LineChartOutlined style={{ fontSize: 50 }} />
                                }
                              />
                            </Col>
                          </Row>
                        )}
                      </Col>
                    </Row>
                    <Row justify="space-between" gutter={[15, 15]}>
                      <Col lg={12} xs={24}>
                        {pams && pams.length > 0 ? (
                          <Row>
                            <Col lg={24} xs={24}>
                              {measurementChart(pams, "pams")}
                            </Col>
                          </Row>
                        ) : (
                          <Row
                            justify="center"
                            align="middle"
                            style={{ width: "100%", height: "100%" }}
                          >
                            <Col lg={24} xs={24}>
                              <Empty
                                description={
                                  <span style={{ fontSize: 14 }}>
                                    {t('medicalConsultations.clinicalRecordModal.pamDisclaimer')}
                                  </span>
                                }
                                image={
                                  <LineChartOutlined style={{ fontSize: 50 }} />
                                }
                              />
                            </Col>
                          </Row>
                        )}
                      </Col>
                      <Col lg={12} xs={24}>
                        {imcs && imcs.length > 0 ? (
                          <Row>
                            <Col lg={24} xs={24}>
                              {measurementChart(imcs, "imcs")}
                            </Col>
                          </Row>
                        ) : (
                          <Row
                            justify="center"
                            align="middle"
                            style={{ width: "100%", height: "100%" }}
                          >
                            <Col lg={24} xs={24}>
                              <Empty
                                description={
                                  <span style={{ fontSize: 14 }}>
                                    {t('medicalConsultations.clinicalRecordModal.imcDisclaimer')}
                                  </span>
                                }
                                image={
                                  <LineChartOutlined style={{ fontSize: 50 }} />
                                }
                              />
                            </Col>
                          </Row>
                        )}
                      </Col>
                    </Row>
                  </Col>

                  <Col
                    lg={9}
                    xs={23}
                    className="item-wrapper p-15"
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    {imcs &&
                      imcs.length > 0 &&
                      heights &&
                      heights.length > 0 ? (
                      <Row align="middle">
                        <Col lg={15} xs={12}>
                          {imcType(imcs)}
                        </Col>
                        <Col
                          lg={9}
                          xs={12}
                          style={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                          }}
                        >
                          <p>
                            {t('medicalConsultations.clinicalRecordModal.heightTitle')}: <br />
                            {heights.slice(-1)[0]["value"]} cms
                          </p>
                        </Col>
                      </Row>
                    ) : (
                      <Row
                        justify="center"
                        align="middle"
                        style={{ width: "100%", height: "100%" }}
                      >
                        <Col lg={24} xs={24}>
                          <Empty
                            description={
                              <span style={{ fontSize: 14 }}>
                                {t('medicalConsultations.clinicalRecordModal.heightDisclaimer')}
                              </span>
                            }
                            image={
                              <LineChartOutlined style={{ fontSize: 50 }} />
                            }
                          />
                        </Col>
                      </Row>
                    )}
                  </Col>
                </Row>
              )}
            </Col>
          </Row>
        </Col>
      </Row>
    </Modal>
  );
};

export default ClinicalRecordModal;